import { AnyAction, ThunkAction } from "@reduxjs/toolkit";
import { RootState } from "./index";
import { advisorContextActions } from "./advisorContext-slice";
import { getAdvisorProfile } from "../services/advisorProfileService";
import { AdvisorProfileResponse } from "../types/advisorProfile.types";

/**
 * Discriminated on purpose. A bare `AdvisorProfileResponse | null` collapses
 * the 404 onboarding state and a failed request into the same `null`, and
 * the two mean opposite things to a caller reconciling its editor against
 * server truth: "the row is gone, drop what you were editing" versus "I
 * learned nothing, keep it".
 */
export type AdvisorProfileFetchResult =
  | { ok: true; profile: AdvisorProfileResponse | null }
  | { ok: false };

/**
 * ONE shared fetch for the advisor profile, consumed by both the profile page
 * and the context loader, so `404 -> create -> activation enabled` converges
 * without a page reload. Never rejects: the failure is recorded in the slice
 * and reported through the result.
 */
export const fetchAdvisorProfile = (
  userId: number,
  signal?: AbortSignal,
): ThunkAction<
  Promise<AdvisorProfileFetchResult>,
  RootState,
  unknown,
  AnyAction
> => {
  return async (dispatch) => {
    dispatch(advisorContextActions.fetchStart());
    try {
      const profile = await getAdvisorProfile(userId, signal);
      dispatch(advisorContextActions.fetchSucceeded(profile));
      return { ok: true, profile };
    } catch (error) {
      console.error("AdvisorContext: profile fetch failed", error);
      dispatch(advisorContextActions.fetchFailed());
      return { ok: false };
    }
  };
};
